import { isAfter, isValid } from "date-fns";
import { backendUrl } from "env";
import { RequestMiddleware } from "graphql-request/build/esm/types";
import jwtDecode from "jwt-decode";
import { DecodedToken, RefreshTokenResponse, Token } from "models/authToken";
import { getStore } from "store";
import { isPartnerView } from "store/root.store";
import { selectAuth, setAuth } from "store/slices/user.store";
import { abortAll } from "./client";

const UnprotectedFields = new Set([
  "auth0Login",
  "preLogin",
  "login",
  "logout",
  "publicFeatureFlags",
  "userEndpoints",
  "databaseCheck",
]);

export const authMiddleware: RequestMiddleware = async (request) => {
  const state = getStore().getState();
  const partnerView = isPartnerView(state);
  if (partnerView) {
    return {
      ...request,
      headers: {
        ...request.headers,
        authorization: `PartnerBearer ${state.partner.token}`,
      },
    };
  } else {
    const authVar = selectAuth(state);
    if (!authVar.isLoggedIn) return request;
    if (request.operationName && UnprotectedFields.has(request.operationName)) return request;
    let token = await getToken();
    return {
      ...request,
      headers: {
        ...request.headers,
        authorization: `Bearer ${token}`,
      },
    };
  }
};

let refreshingPromise: Promise<Token | undefined> = Promise.resolve(undefined);
export const resetAuthRefresh = () => {
  refreshingPromise = Promise.resolve(undefined);
};

const getToken = (): Promise<Token | undefined> => {
  refreshingPromise = refreshingPromise.then(refreshCore);
  return refreshingPromise;
};

const refreshCore = (token: Token | undefined) => {
  const authVar = selectAuth(getStore().getState());
  token = token || authVar.token;
  if (!isTokenExpired(token, "exp") && !isTokenExpired(token, "accExp")) {
    return token;
  }
  return refreshToken(token);
};

const isTokenExpired = (token: Token | undefined, expiryField: "exp" | "accExp") => {
  if (!token) return true;
  let decodedToken = jwtDecode<DecodedToken>(token);
  let expiry = decodedToken[expiryField];
  expiry = typeof expiry === "string" ? Number.parseFloat(expiry) : expiry;
  if (!expiry) return true;
  if (!isValid(new Date(expiry))) return true;
  if (isAfter(new Date(Date.now()), new Date(expiry))) return true;
  return false;
};

const refreshToken = async (token: string | undefined): Promise<Token | undefined> => {
  const authVar = selectAuth(getStore().getState());
  try {
    if (!token) return;
    const auth = await fetch(`${backendUrl()}/refresh`, {
      method: "POST",
      headers: {
        authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }).then(async (res) => {
      const data = (await res.json()) as RefreshTokenResponse;
      if (!res.ok || !data.auth) {
        const error = new Error(data?.message || res.statusText || "Error");
        return Promise.reject(error);
      }
      return data.auth;
    });
    getStore().dispatch(
      setAuth({
        ...authVar,
        token: auth?.token,
        expiry: new Date(auth?.expiry).toISOString(),
      })
    );
    localStorage.setItem("authState", JSON.stringify(auth));
    return auth.token;
  } catch (e) {
    abortAll("TOKEN_EXPIRED");
    getStore().dispatch(
      setAuth({
        ...authVar,
        isLoggedIn: false,
        message: "Your session has expired, please log in again",
      })
    );
    window.location.replace(window.location.origin + "/logout");
    return Promise.reject(e);
  }
};
